/**
 * Hook para gerenciar uma conversa individual
 * 
 * Encapsula toda a lógica de busca, envio de mensagens, realtime, etc.
 * Reutilizável tanto na página Chat.tsx quanto no modal do Kanban.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useProviderUtils } from '@/hooks/useProviderUtils';
import { useContactPresence } from '@/hooks/useContactPresence';
import { useBlacklist } from '@/hooks/useBlacklist';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';
// Conversão de áudio no cliente (OGG/Opus -> MP3) para inboxes Meta WABA
import { prepareAudioForProvider } from '@/lib/audio/convertToMp3';
import { createErrorMetadata } from '@/lib/errorMetadata';
import { registerSendError } from '@/hooks/useErrorCatalog';
import { sendDocumentToConversation } from '@/lib/chat/sendDocument';
import { Database } from '@/integrations/supabase/types';
import { isEligibleForSessionWindow } from '@/lib/instagramSessionWindow';
import type { Conversation, Message, MessageReaction, MessageAttachment, Contact } from '@/pages/tenant/Chat';

// Logger para o módulo (logs condicionais por ambiente)
const chatLogger = createLogger('useChatConversation');

type ConversationStatus = Database['public']['Enums']['conversation_status'];
type MessageType = Database['public']['Enums']['message_type'];
type SenderType = Database['public']['Enums']['sender_type'];

/**
 * Gera delay aleatório para indicador de digitação/gravação
 */
const getRandomDelay = (min: number, max: number): number => {
  return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Normaliza ID para comparação consistente de reações
 */
const normalizeId = (id: string): string => {
  return id.replace(/\D/g, '');
};

/**
 * Aplica assinatura do agente NO INÍCIO da mensagem se configurado na inbox
 * @param content Conteúdo original da mensagem
 * @param inboxSettings Configurações da inbox (signature_enabled, signature_format)
 * @param agentName Nome do agente logado
 * @returns Conteúdo com assinatura aplicada ou original se desabilitado
 */
const applySignature = (
  content: string,
  inboxSettings: { signature_enabled?: boolean; signature_format?: string; [key: string]: unknown } | null | undefined,
  agentName: string | null | undefined
): string => {
  // Se assinatura não está habilitada ou não há nome do agente, retorna conteúdo original
  if (!inboxSettings?.signature_enabled || !agentName) {
    return content;
  }
  
  // Formato padrão: nome em negrito + itálico com dois pontos
  const format = inboxSettings.signature_format || '_*{{agent_name}}:*_';
  const signature = format.replace('{{agent_name}}', agentName);
  
  // Assinatura no INÍCIO + linha em branco + conteúdo
  return `${signature}\n\n${content}`;
};

interface UseChatConversationOptions {
  conversationId: string | null;
  /** Se true, NÃO marca mensagens como lidas (modo observador) */
  isObserverMode?: boolean;
}

/**
 * Hook para gerenciar uma conversa individual
 */
// Constantes de paginação para carregamento otimizado
const MESSAGES_PAGE_SIZE = 10;       // Mensagens por página (load more) — reduzido de 30 para 10
const MESSAGES_INITIAL_LOAD = 10;   // Carga inicial ao abrir a conversa — reduzido de 30 para 10

export function useChatConversation({ conversationId, isObserverMode = false }: UseChatConversationOptions) {
  const { currentTenant, user, profile } = useAuth();
  const { sendTypingIndicator, markMessageAsRead, checkPhoneExists, sendReaction, deleteMessage } = useProviderUtils();
  
  // Hook para gerenciar blacklist LGPD (integração automática com spam)
  const { addToBlacklist } = useBlacklist();
  
  // Estados principais
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(false);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [showSidebar, setShowSidebar] = useState(true);
  
  // Estados de paginação
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

  // ----------------------------------------------------------------------
  // Jump-to-message / Anchor window (pesquisa contextual)
  // ----------------------------------------------------------------------
  // Quando o usuário clica em um resultado da pesquisa, carregamos uma
  // janela pequena (~51 msgs) centrada na mensagem alvo. Nesse modo:
  //  - hasMoreNewer pode ser true (existem mensagens mais recentes que
  //    o fim da janela carregada)
  //  - O realtime NÃO faz append direto; apenas incrementa o contador
  //    `newMessagesWhileAnchored`, exibindo uma pílula "Ir para o fim".
  // ----------------------------------------------------------------------
  const [hasMoreNewer, setHasMoreNewer] = useState(false);
  const [loadingMoreNewer, setLoadingMoreNewer] = useState(false);
  const [isAnchoredMode, setIsAnchoredMode] = useState(false);
  const [newMessagesWhileAnchored, setNewMessagesWhileAnchored] = useState(0);
  const [jumpingToMessageId, setJumpingToMessageId] = useState<string | null>(null);

  // Estado para contagem total de mensagens (exibir "X de Y mensagens")
  const [totalMessagesCount, setTotalMessagesCount] = useState<number | null>(null);
  
  // Ref para timeout de digitação
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Ref para evitar chamadas duplicadas de fetch (race condition protection)
  const fetchingConversationRef = useRef(false);
  const fetchingMessagesRef = useRef(false);
  
  // Ref para acessar messages sem dependência em useCallback (evita loop infinito)
  const messagesRef = useRef<Message[]>([]);

  // Ref espelho de isAnchoredMode (lido dentro do handler de realtime sem
  // criar dependência no useEffect, evitando recriar a subscription)
  const isAnchoredModeRef = useRef(false);
  useEffect(() => {
    isAnchoredModeRef.current = isAnchoredMode;
  }, [isAnchoredMode]);
  
  // Ref para modo observador (evita stale closure em callbacks)
  const isObserverModeRef = useRef(isObserverMode);
  useEffect(() => {
    isObserverModeRef.current = isObserverMode;
  }, [isObserverMode]);

  // Contact presence (typing/recording indicator)
  const contactPresence = useContactPresence(
    conversation?.inbox_id ?? null,
    conversation?.contact?.phone ?? null,
    conversation?.inbox?.provider_slug
  );

  // ==========================
  // FUNÇÕES DE BUSCA
  // ==========================

  /**
   * Busca dados completos de uma conversa
   * NÃO reseta conversation para null durante fetch (evita flickering)
   */
  const fetchConversation = useCallback(async () => {
    // Apenas retorna se não há conversationId - NÃO reseta conversation aqui
    if (!conversationId) return;
    
    // Evita chamadas duplicadas (race condition)
    if (fetchingConversationRef.current) return;
    fetchingConversationRef.current = true;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('conversations')
        .select(`
          id,
          inbox_id,
          contact_id,
          group_id,
          assigned_to,
          department_id,
          status,
          last_message_at,
          last_message_preview,
          unread_count,
          created_at,
          has_non_campaign_messages,
          last_inbound_at,
          bot_paused,
          bot_paused_at,
          bot_paused_until,
          kanban_phase_id,
          contacts (
            id,
            name,
            phone,
            email,
            avatar_url,
            is_saved_to_agenda,
            channel_ids,
            metadata
          ),
          inboxes (
            id,
            name,
            channel_type,
            identifier,
            settings,
            api_providers:provider_id (
              slug
            )
          ),
          departments (
            id,
            name
          ),
          profiles:assigned_to (
            id,
            full_name,
            avatar_url
          )
        `)
        .eq('id', conversationId)
        .single();

      if (error) throw error;

      // Busca metadados do grupo via RPC server-side (evita SELECT direto em chat_groups)
      let groupData: {
        id: string;
        name: string | null;
        description: string | null;
        avatar_url: string | null;
        participant_count: number | null;
        external_id: string | null;
        is_readonly: boolean | null;
        is_announcement: boolean | null;
      } | null = null;
      if (data.group_id && currentTenant?.id) {
        const { data: groupRows, error: groupError } = await supabase.rpc('get_chat_group_safe', {
          p_group_id: data.group_id,
          p_tenant_hint: currentTenant.id,
        });
        if (groupError) {
          chatLogger.warn('Falha ao buscar metadados do grupo', groupError);
        } else if (groupRows && groupRows.length > 0) {
          groupData = groupRows[0];
        }
      }

      // Busca tags do contato se existir
      let contactTags: Array<{ id: string; name: string; color: string }> = [];
      if (data.contact_id) {
        const { data: tagAssignments } = await supabase
          .from('contact_tag_assignments')
          .select('contact_tags(id, name, color)')
          .eq('contact_id', data.contact_id);

        contactTags = (tagAssignments || [])
          .map(ta => ({
            id: ta.contact_tags?.id || '',
            name: ta.contact_tags?.name || '',
            color: ta.contact_tags?.color || '#6366f1',
          }))
          .filter(t => t.id);
      }

      // Formata a conversa
      const formattedConversation: Conversation = {
        id: data.id,
        inbox_id: data.inbox_id,
        contact_id: data.contact_id,
        // Inclui channel_ids e metadata para exibir @username do Instagram no modal Kanban
        contact: data.contacts ? {
          id: data.contacts.id,
          name: data.contacts.name,
          phone: data.contacts.phone,
          email: data.contacts.email,
          avatar_url: data.contacts.avatar_url,
          is_saved_to_agenda: data.contacts.is_saved_to_agenda,
          channel_ids: data.contacts.channel_ids as Contact['channel_ids'],
          metadata: data.contacts.metadata as Record<string, unknown> | null,
          tags: contactTags,
        } : null,
        group_id: data.group_id,
        group: groupData ? {
          id: groupData.id,
          name: groupData.name,
          description: groupData.description,
          avatar_url: groupData.avatar_url,
          participant_count: groupData.participant_count,
          external_id: groupData.external_id,
          is_readonly: groupData.is_readonly,
          is_announcement: groupData.is_announcement,
        } : null,
        inbox: {
          id: data.inboxes?.id || '',
          name: data.inboxes?.name || '',
          channel_type: data.inboxes?.channel_type || '',
          identifier: data.inboxes?.identifier || '',
          settings: data.inboxes?.settings as {
            signature_enabled?: boolean;
            signature_format?: string;
            [key: string]: unknown;
          } | null,
          // Provider slug via join com api_providers para match no catálogo de erros
          provider_slug: (data.inboxes as any)?.api_providers?.slug || null,
        },
        department_id: data.department_id,
        department: data.departments ? {
          id: data.departments.id,
          name: data.departments.name,
        } : null,
        assigned_to: data.assigned_to,
        // Acessa dados do agente via propriedade 'profiles' retornada pelo Supabase
        assigned_agent: (data as any).profiles ? {
          id: (data as any).profiles.id,
          full_name: (data as any).profiles.full_name,
          avatar_url: (data as any).profiles.avatar_url,
        } : null,
        status: data.status,
        last_message_at: data.last_message_at,
        last_message_preview: data.last_message_preview,
        unread_count: data.unread_count,
        created_at: data.created_at,
        has_non_campaign_messages: data.has_non_campaign_messages ?? true,
        // Timestamp da última mensagem inbound (coluna física para janela 24h WhatsApp Oficial)
        last_inbound_at: data.last_inbound_at || null,
        // Human Takeover: estado de pausa do bot
        bot_paused: data.bot_paused || false,
        bot_paused_at: data.bot_paused_at || null,
        bot_paused_until: data.bot_paused_until || null,
        // kanban_phase_id mantido p/ compat. O objeto kanban_phase completo é
        // resolvido pelo popover do header via useContactSidebarDetails (RPC),
        // já que kanban_phases não possui GRANT SELECT para authenticated.
        kanban_phase_id: (data as any).kanban_phase_id ?? null,
      };

      setConversation(formattedConversation);
    } catch (error) {
      chatLogger.error('Erro ao buscar conversa:', error);
      toast.error('Erro ao carregar conversa');
    } finally {
      setLoading(false);
      fetchingConversationRef.current = false;
    }
  }, [conversationId]);

  /**
   * Busca mensagens da conversa com PAGINAÇÃO
   * Carrega as N mensagens mais recentes primeiro, permitindo carregar mais antigas depois
   * 
   * @param loadMore - Se true, carrega mensagens anteriores às já carregadas
   */
  const fetchMessages = useCallback(async (loadMore = false) => {
    // Apenas retorna se não há conversationId - NÃO reseta messages aqui
    if (!conversationId) return;
    
    // Evita chamadas duplicadas (race condition)
    if (!loadMore && fetchingMessagesRef.current) return;
    if (!loadMore) fetchingMessagesRef.current = true;

    try {
      if (loadMore) {
        setLoadingMoreMessages(true);
      } else {
        setMessagesLoading(true);
      }

      const pageSize = loadMore ? MESSAGES_PAGE_SIZE : MESSAGES_INITIAL_LOAD;

      // Cursor para load more: timestamp da mensagem mais antiga atualmente carregada
      const beforeSentAt = loadMore && messagesRef.current.length > 0
        ? messagesRef.current[0].sent_at
        : null;

      // SERVER-SIDE: uma única chamada RPC traz mensagens + attachments + reply_to + sender
      // Substitui SELECT direto em messages/message_attachments para reduzir carga no banco
      const { data, error } = await supabase.rpc('get_conversation_messages_page', {
        p_conversation_id: conversationId,
        p_before_sent_at: beforeSentAt,
        p_page_size: pageSize,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      if (error) throw error;

      // RPC retorna DESC; reverte para ordem cronológica (ascendente) para exibição
      const orderedData = (data || []).slice().reverse();

      // Verifica se há mais mensagens para carregar
      setHasMoreMessages((data?.length || 0) === pageSize);

      // Formata as mensagens (RPC já entrega attachments/reply_to/sender prontos)
      const formattedMessages: Message[] = orderedData.map((msg: any) => ({
        id: msg.id,
        conversation_id: msg.conversation_id,
        sender_type: msg.sender_type,
        sender_id: msg.sender_id,
        content: msg.content,
        message_type: msg.message_type,
        direction: msg.direction,
        status: msg.status,
        sent_at: msg.sent_at,
        delivered_at: msg.delivered_at,
        read_at: msg.read_at,
        external_id: msg.external_id,
        reply_to_id: msg.reply_to_id,
        attachments: (msg.attachments || []).map((att: any) => ({
          ...att,
          metadata: (att.metadata ?? null) as Record<string, unknown> | null,
        })),
        reply_to: msg.reply_to
          ? {
              id: msg.reply_to.id,
              content: msg.reply_to.content,
              message_type: msg.reply_to.message_type as MessageType,
              sender_type: msg.reply_to.sender_type as SenderType,
              attachments: (msg.reply_to.attachments || []).map((att: any) => ({
                ...att,
                metadata: null,
              })),
            }
          : null,
        reactions: Array.isArray(msg.reactions) ? (msg.reactions as unknown as MessageReaction[]) : null,
        metadata: msg.metadata as Record<string, unknown> | null,
        deleted_at: msg.deleted_at,
        participant_phone: msg.participant_phone,
        sender: msg.sender
          ? {
              id: msg.sender.id,
              full_name: msg.sender.full_name,
              avatar_url: msg.sender.avatar_url,
            }
          : null,
      }));

      // Merge mensagens: prepend para load more, replace para carga inicial
      if (loadMore) {
        setMessages(prev => [...formattedMessages, ...prev]);
      } else {
        setMessages(formattedMessages);
        // Carga inicial sempre sai do modo ancorado (volta ao "modo padrão":
        // últimas N mensagens, sem mensagens "mais novas" pendentes).
        setIsAnchoredMode(false);
        setHasMoreNewer(false);
        setNewMessagesWhileAnchored(0);
      }
    } catch (error) {
      chatLogger.error('Erro ao buscar mensagens:', error);
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoadingMoreMessages(false);
      setMessagesLoading(false);
      fetchingMessagesRef.current = false;
    }
  }, [conversationId, currentTenant?.id]); // REMOVIDO 'messages' para evitar loop infinito - usa messagesRef

  /**
   * Carrega mais mensagens antigas (scroll infinito reverso)
   */
  const loadMoreMessages = useCallback(async () => {
    if (!hasMoreMessages || loadingMoreMessages) return;
    await fetchMessages(true);
  }, [hasMoreMessages, loadingMoreMessages, fetchMessages]);

  // ----------------------------------------------------------------------
  // Helper: mapeia uma linha bruta da RPC para o tipo Message do app.
  // Reaproveitado por fetchMessages, jumpToMessage e loadMoreNewer para
  // garantir formato 100% idêntico em todos os caminhos.
  // ----------------------------------------------------------------------
  const mapRpcRowToMessage = useCallback((msg: any): Message => ({
    id: msg.id,
    conversation_id: msg.conversation_id,
    sender_type: msg.sender_type,
    sender_id: msg.sender_id,
    content: msg.content,
    message_type: msg.message_type,
    direction: msg.direction,
    status: msg.status,
    sent_at: msg.sent_at,
    delivered_at: msg.delivered_at,
    read_at: msg.read_at,
    external_id: msg.external_id,
    reply_to_id: msg.reply_to_id,
    attachments: (msg.attachments || []).map((att: any) => ({
      ...att,
      metadata: (att.metadata ?? null) as Record<string, unknown> | null,
    })),
    reply_to: msg.reply_to
      ? {
          id: msg.reply_to.id,
          content: msg.reply_to.content,
          message_type: msg.reply_to.message_type as MessageType,
          sender_type: msg.reply_to.sender_type as SenderType,
          attachments: (msg.reply_to.attachments || []).map((att: any) => ({
            ...att,
            metadata: null,
          })),
        }
      : null,
    reactions: Array.isArray(msg.reactions) ? (msg.reactions as unknown as MessageReaction[]) : null,
    metadata: msg.metadata as Record<string, unknown> | null,
    deleted_at: msg.deleted_at,
    participant_phone: msg.participant_phone,
    sender: msg.sender
      ? {
          id: msg.sender.id,
          full_name: msg.sender.full_name,
          avatar_url: msg.sender.avatar_url,
        }
      : null,
  }), []);

  /**
   * Jump-to-message: carrega uma janela ancorada (~51 msgs) centrada na
   * mensagem alvo, SUBSTITUINDO completamente a lista atual (custo constante,
   * padrão WhatsApp Web / Slack / Telegram).
   *
   * Se a mensagem alvo já estiver no DOM, retorna true sem fetch.
   */
  const jumpToMessage = useCallback(async (messageId: string): Promise<boolean> => {
    if (!conversationId) {
      chatLogger.warn('[jumpToMessage/hook] sem conversationId');
      return false;
    }

    // Atalho: mensagem já carregada — sem RPC
    if (messagesRef.current.some(m => m.id === messageId)) {
      return true;
    }

    // Captura conversa de origem para abortar se trocar durante o await
    const convAtStart = conversationId;
    chatLogger.info('[jumpToMessage/hook] start', {
      conversationId: convAtStart,
      messageId,
      tenant: currentTenant?.id ?? null,
    });

    try {
      setJumpingToMessageId(messageId);
      chatLogger.info('[jumpToMessage/hook] calling RPC get_conversation_messages_around');

      const { data, error } = await supabase.rpc('get_conversation_messages_around', {
        p_conversation_id: convAtStart,
        p_anchor_message_id: messageId,
        p_before_count: 25,
        p_after_count: 25,
        p_tenant_hint: currentTenant?.id ?? null,
      });

      // Aborta se a conversa foi trocada enquanto a RPC estava em voo
      if (conversationId !== convAtStart) {
        chatLogger.warn('[jumpToMessage/hook] conversa trocou durante a RPC, descartando');
        return false;
      }

      if (error) throw error;
      if (!data || data.length === 0) {
        chatLogger.warn('[jumpToMessage/hook] RPC retornou vazio', { messageId });
        toast.error('Mensagem não encontrada nesta conversa.');
        return false;
      }

      const windowMsgs: Message[] = (data as any[]).map(mapRpcRowToMessage);
      const flags = (data as any[])[0];

      setMessages(windowMsgs);
      setIsAnchoredMode(true);
      setHasMoreMessages(Boolean(flags?.has_more_older));
      setHasMoreNewer(Boolean(flags?.has_more_newer));
      setNewMessagesWhileAnchored(0);

      return true;
    } catch (err: any) {
      chatLogger.error('[jumpToMessage/hook] erro', err);
      const reason =
        err?.message ??
        err?.error_description ??
        err?.details ??
        (typeof err === 'string' ? err : 'erro desconhecido');
      toast.error(`Não foi possível localizar a mensagem: ${reason}`);
      return false;
    } finally {
      setJumpingToMessageId(null);
    }
  }, [conversationId, currentTenant?.id, mapRpcRowToMessage]);

  /**
   * Carrega N mensagens MAIS NOVAS que a última carregada.
   * Usado em modo ancorado para o usuário "descer" a partir do resultado
   * da pesquisa até o fim real da conversa.
   */
  const loadMoreNewer = useCallback(async (): Promise<void> => {
    if (!conversationId || !hasMoreNewer || loadingMoreNewer) return;
    const last = messagesRef.current[messagesRef.current.length - 1];
    if (!last) return;

    try {
      setLoadingMoreNewer(true);

      const { data, error } = await supabase.rpc('get_conversation_messages_page', {
        p_conversation_id: conversationId,
        p_before_sent_at: null,
        p_page_size: MESSAGES_PAGE_SIZE,
        p_tenant_hint: currentTenant?.id ?? null,
        p_after_sent_at: last.sent_at,
      });

      if (error) throw error;

      // RPC retorna DESC; reverte para ASC e faz append cronológico
      const newer = (data || []).slice().reverse().map(mapRpcRowToMessage);

      setMessages(prev => {
        const seen = new Set(prev.map(m => m.id));
        const deduped = newer.filter(m => !seen.has(m.id));
        return [...prev, ...deduped];
      });

      // Se trouxe menos que o page_size, alcançou o fim real
      setHasMoreNewer((data?.length || 0) === MESSAGES_PAGE_SIZE);

      // Se alcançou o fim, sai do modo ancorado automaticamente
      if ((data?.length || 0) < MESSAGES_PAGE_SIZE) {
        setIsAnchoredMode(false);
        setNewMessagesWhileAnchored(0);
      }
    } catch (err) {
      chatLogger.error('Erro no loadMoreNewer:', err);
      toast.error('Erro ao carregar mensagens mais recentes.');
    } finally {
      setLoadingMoreNewer(false);
    }
  }, [conversationId, currentTenant?.id, hasMoreNewer, loadingMoreNewer, mapRpcRowToMessage]);

  /**
   * Sai do modo ancorado: recarrega as últimas N mensagens (modo padrão)
   * e zera contadores. Usado pelo botão "Ir para o fim".
   */
  const exitAnchorMode = useCallback(async (): Promise<void> => {
    await fetchMessages(false);
  }, [fetchMessages]);

  /**
   * Busca contagem total de mensagens da conversa via RPC
   * Usado para exibir "X de Y mensagens" sem carregar todas
   */
  const fetchMessageCount = useCallback(async () => {
    if (!conversationId) {
      setTotalMessagesCount(null);
      return;
    }
    
    try {
      const { data, error } = await supabase.rpc('count_conversation_messages', {
        _conversation_id: conversationId,
      });
      
      if (error) {
        chatLogger.error('Erro ao buscar contagem de mensagens:', error);
        return;
      }
      
      setTotalMessagesCount(data ?? null);
    } catch (error) {
      chatLogger.error('Erro ao buscar contagem de mensagens:', error);
    }
  }, [conversationId]);

  /**
   * Marca conversa como lida
   */
  const markAsRead = useCallback(async () => {
    if (!conversationId) return;

    try {
      await supabase.rpc('mark_conversation_as_read', {
        _conversation_id: conversationId,
      });
      
      setConversation(prev => prev ? { ...prev, unread_count: 0 } : null);
    } catch (error) {
      chatLogger.error('Erro ao marcar como lida:', error);
    }
  }, [conversationId]);

  // ==========================
  // HANDLERS DE ENVIO
  // ==========================

  /**
   * Envia mensagem de texto
   * @param content Conteúdo da mensagem
   * @param replyToExternalId external_id da mensagem sendo respondida (para Z-API)
   * @param replyToProviderMessageId providerMessageId da mensagem (para Notificame)
   */
  const handleSendMessage = useCallback(async (content: string, replyToExternalId?: string, replyToProviderMessageId?: string) => {
    if (!conversation || !user) return;

    try {
      // Aplica assinatura do agente se habilitado na inbox
      const contentWithSignature = applySignature(
        content,
        conversation.inbox?.settings,
        profile?.full_name
      );

      // Busca reply_to_id interno via RPC server-side (escopada por conversa)
      let replyToId: string | null = null;
      if (replyToExternalId) {
        const { data: resolvedId } = await supabase.rpc('resolve_reply_to_id_by_external', {
          p_conversation_id: conversation.id,
          p_external_id: replyToExternalId,
          p_tenant_hint: currentTenant?.id ?? null,
        });
        replyToId = (resolvedId as string | null) || null;
      }

      // Busca dados da mensagem respondida para exibir quote no balão otimista
      let replyToData: Message['reply_to'] = null;
      if (replyToId) {
        const originalMsg = messages.find(m => m.id === replyToId);
        if (originalMsg) {
          replyToData = {
            id: originalMsg.id,
            content: originalMsg.content,
            message_type: originalMsg.message_type,
            sender_type: originalMsg.sender_type,
            attachments: originalMsg.attachments,
          };
        }
      }

      // 1. Insert message with pending status (com assinatura aplicada)
      const { data: messageData, error: insertError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_type: 'agent',
          sender_id: user.id,
          content: contentWithSignature,
          message_type: 'text',
          direction: 'outbound',
          status: 'pending',
          reply_to_id: replyToId,
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // OTIMISTA: Adiciona mensagem ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type,
        direction: messageData.direction,
        status: 'sent', // Otimista - mostra como enviado imediatamente
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        reply_to_id: messageData.reply_to_id,
        attachments: [],
        reply_to: replyToData,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // Determina qual ID usar para reply baseado no provider
      // Notificame (whatsapp_oficial): usa providerMessageId (Base64 do wamid)
      // Z-API (whatsapp): usa external_id
      const isNotificame = conversation.inbox?.channel_type === 'whatsapp_oficial';
      const replyToParam = isNotificame && replyToProviderMessageId 
        ? replyToProviderMessageId 
        : replyToExternalId;

      // 2. Envia via edge function unificada em background (com IDs de correlação para observabilidade)
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: conversation.inbox_id,
          phone: conversation.contact?.phone || conversation.group?.external_id || '',
          content: contentWithSignature,
          message_type: 'text',
          reply_to: replyToParam,
          delay_typing: getRandomDelay(1, 5),
          // Campos de rastreabilidade para observabilidade
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: conversation.id,
          contact_id: conversation.contact?.id,
        },
      });

      // 3. Update message status e metadata
      if (sendError || !sendResult?.success) {
        // Estrutura de erro para persistência e exibição no ErrorDetailsDialog
        const errorMetadata = {
          error: {
            code: sendResult?.error_code || (sendError as any)?.code || '500',
            title: sendResult?.error || (sendError as Error)?.message || 'Erro ao enviar mensagem',
            details: sendResult?.details || sendResult?.error || (sendError as Error)?.message,
            message: JSON.stringify(sendResult?.provider_response || sendError),
            occurred_at: new Date().toISOString(),
          }
        };
        
        await supabase
          .from('messages')
          .update({ 
            status: 'failed',
            metadata: errorMetadata 
          })
          .eq('id', messageData.id);
        
        // OTIMISTA: Reverte para 'failed' no state local COM metadata de erro
        setMessages(prev => prev.map(m => 
          m.id === messageData.id ? { ...m, status: 'failed', metadata: errorMetadata } : m
        ));
        
        // Registro assíncrono no catálogo de erros (fire-and-forget)
        registerSendError({
          errorCode: sendResult?.error_code || (sendError as any)?.code || '500',
          provider: conversation.inbox?.provider_slug || 'unknown',
          inboxId: conversation.inbox_id,
          messageId: messageData.id,
          conversationId: conversation.id,
          messageType: 'text',
          rawError: sendResult || sendError,
          providerMessage: sendResult?.error || (sendError as Error)?.message,
        });
        
        toast.error('Erro ao enviar mensagem via WhatsApp');
      } else {
        // Monta update com providerMessageId para uso em reações (Notificame)
        const updateData: {
          status: 'sent';
          external_id?: string;
          metadata?: { providerMessageId: string };
        } = {
          status: 'sent',
          external_id: sendResult.external_id,
        };
        
        if (sendResult.provider_message_id) {
          updateData.metadata = { providerMessageId: sendResult.provider_message_id };
        }
        
        await supabase
          .from('messages')
          .update(updateData)
          .eq('id', messageData.id);
        
        // OTIMISTA: Atualiza external_id e metadata no state local
        setMessages(prev => prev.map(m => 
          m.id === messageData.id 
            ? { ...m, external_id: sendResult.external_id, metadata: updateData.metadata || null } 
            : m
        ));
      }
    } catch (error) {
      chatLogger.error('Erro ao enviar mensagem:', error);
      toast.error('Erro ao enviar mensagem');
    }
  }, [conversation, user, profile]);

  /**
   * Envia múltiplas imagens
   */
  const handleSendImages = useCallback(async (files: File[], caption?: string) => {
    if (!conversation || !user || files.length === 0) return;

    try {
      // 1. Upload de todas as imagens em paralelo
      const uploadResults = await Promise.all(
        files.map(async (file, index) => {
          const timestamp = Date.now() + index;
          const extension = file.name.split('.').pop() || 'jpg';
          const storagePath = `Chats/${conversation.id}/image_${timestamp}.${extension}`;
          
          const { error: uploadError } = await supabase.storage
            .from('anexos')
            .upload(storagePath, file, {
              contentType: file.type,
              upsert: false,
            });

          if (uploadError) throw uploadError;

          const { data: publicUrlData } = supabase.storage
            .from('anexos')
            .getPublicUrl(storagePath);

          return { 
            storagePath,
            publicUrl: publicUrlData.publicUrl, 
            file,
            mimeType: file.type,
            sizeBytes: file.size,
          };
        })
      );

      // 2. Gera preview
      const contentText = files.length > 1 
        ? `📷 ${files.length} imagens${caption ? ` - ${caption}` : ''}`
        : caption || '📷 Imagem';

      // 3. Cria mensagem
      const { data: messageData, error: insertError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_type: 'agent',
          sender_id: user.id,
          content: contentText,
          message_type: 'image',
          direction: 'outbound',
          status: 'pending',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // 4. Cria attachments
      const attachmentsData = uploadResults.map(({ storagePath, mimeType, sizeBytes }) => ({
        message_id: messageData.id,
        type: 'image' as const,
        url: storagePath,
        mime_type: mimeType,
        size_bytes: sizeBytes,
      }));

      await supabase.from('message_attachments').insert(attachmentsData);

      // OTIMISTA: Adiciona mensagem ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type,
        direction: messageData.direction,
        status: 'sent', // Otimista
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        reply_to_id: null,
        attachments: attachmentsData.map((att, idx) => ({
          id: `temp-${idx}`,
          ...att,
          filename: null,
          metadata: null,
        })),
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 5. Envia cada imagem
      let allSuccess = true;
      let firstExternalId: string | null = null;
      // Variável para armazenar erro da primeira falha para exibição
      let lastSendError: { sendResult?: any; sendError?: any } | null = null;

      for (let i = 0; i < uploadResults.length; i++) {
        const { publicUrl } = uploadResults[i];
        const imageCaption = i === 0 ? caption : undefined;
        
        // Extrai informações do arquivo para envio correto via WAHA
        const currentFile = uploadResults[i].file;
        const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
          body: {
            inbox_id: conversation.inbox_id,
            phone: conversation.contact?.phone || conversation.group?.external_id || '',
            message_type: 'image',
            media_url: publicUrl,
            media_caption: imageCaption,
            // Campos obrigatórios para WAHA: mimetype e filename
            media_mime_type: currentFile.type || 'image/jpeg',
            media_filename: currentFile.name || `image_${i}.jpg`,
            // Campos de rastreabilidade para observabilidade
            source_type: 'chat',
            message_id: messageData.id,
            conversation_id: conversation.id,
            contact_id: conversation.contact?.id,
          },
        });

        if (sendError || !sendResult?.success) {
          allSuccess = false;
          // Armazena erro da primeira falha para exibição
          if (!lastSendError) {
            lastSendError = { sendResult, sendError };
          }
        } else {
          if (i === 0) {
            firstExternalId = sendResult.external_id;
          }
        }
      }

      // 6. Atualiza status com metadata de erro se falhou
      const finalStatus = allSuccess ? 'sent' : 'failed';
      
      // Se falhou, monta metadata de erro para exibição no ErrorDetailsDialog
      let errorMetadata: { error: { code: string; title: string; details: string; message: string; occurred_at: string } } | null = null;
      if (!allSuccess && lastSendError) {
        const { sendResult, sendError } = lastSendError;
        errorMetadata = {
          error: {
            code: sendResult?.error_code || (sendError as any)?.code || '500',
            title: sendResult?.error || (sendError as Error)?.message || 'Erro ao enviar imagem',
            details: sendResult?.details || sendResult?.error || (sendError as Error)?.message,
            message: JSON.stringify(sendResult?.provider_response || sendError),
            occurred_at: new Date().toISOString(),
          }
        };
      }
      
      const updateData: {
        status: 'sent' | 'failed';
        external_id?: string;
        metadata?: { error: { code: string; title: string; details: string; message: string; occurred_at: string } };
      } = {
        status: finalStatus,
        external_id: firstExternalId ?? undefined,
        ...(errorMetadata && { metadata: errorMetadata }),
      };

      await supabase
        .from('messages')
        .update(updateData)
        .eq('id', messageData.id);

      // OTIMISTA: Atualiza state local com resultado final e metadata de erro
      setMessages(prev => prev.map(m => 
        m.id === messageData.id 
          ? { ...m, status: finalStatus, external_id: firstExternalId, metadata: errorMetadata } 
          : m
      ));

      if (!allSuccess) {
        toast.error('Erro ao enviar algumas imagens');
      }
    } catch (error) {
      chatLogger.error('Erro ao enviar imagens:', error);
      toast.error('Erro ao enviar imagens');
    }
  }, [conversation, user, profile]);

  /**
   * Envia vídeo com legenda opcional
   * Limite de 50MB conforme especificação do WhatsApp
   */
  const handleSendVideo = useCallback(async (file: File, caption?: string) => {
    if (!conversation || !user) return;

    try {
      // 1. Upload do vídeo para Supabase Storage
      const timestamp = Date.now();
      const extension = file.name.split('.').pop() || 'mp4';
      const storagePath = `Chats/${conversation.id}/video_${timestamp}.${extension}`;
      
      const { error: uploadError } = await supabase.storage
        .from('anexos')
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('anexos')
        .getPublicUrl(storagePath);

      // 2. Gera preview para a conversa
      const contentText = caption || '🎥 Vídeo';

      // 3. Cria mensagem com status 'pending'
      const { data: messageData, error: insertError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_type: 'agent',
          sender_id: user.id,
          content: contentText,
          message_type: 'video',
          direction: 'outbound',
          status: 'pending',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      // 4. Cria attachment
      const attachmentData = {
        message_id: messageData.id,
        type: 'video' as const,
        url: storagePath,
        mime_type: file.type,
        filename: file.name,
        size_bytes: file.size,
      };

      await supabase.from('message_attachments').insert(attachmentData);

      // OTIMISTA: Adiciona mensagem ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type,
        direction: messageData.direction,
        status: 'sent', // Otimista
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        reply_to_id: null,
        attachments: [{ id: 'temp-0', ...attachmentData, metadata: null }],
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      // 5. Envia via provider
      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: conversation.inbox_id,
          phone: conversation.contact?.phone || conversation.group?.external_id || '',
          message_type: 'video',
          media_url: publicUrlData.publicUrl,
          media_caption: caption,
          // Campos para providers como WAHA
          media_mime_type: file.type || 'video/mp4',
          media_filename: file.name || `video_${timestamp}.mp4`,
          // Campos de rastreabilidade
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: conversation.id,
          contact_id: conversation.contact?.id,
        },
      });

      // 6. Atualiza status final
      const finalStatus = (sendError || !sendResult?.success) ? 'failed' : 'sent';
      const externalId = sendResult?.external_id || null;

      await supabase
        .from('messages')
        .update({ status: finalStatus, external_id: externalId })
        .eq('id', messageData.id);

      // OTIMISTA: Atualiza state local com resultado final
      setMessages(prev => prev.map(m => 
        m.id === messageData.id 
          ? { ...m, status: finalStatus, external_id: externalId } 
          : m
      ));

      if (finalStatus === 'failed') {
        toast.error('Erro ao enviar vídeo');
      }
    } catch (error) {
      chatLogger.error('Erro ao enviar vídeo:', error);
      toast.error('Erro ao enviar vídeo');
    }
  }, [conversation, user, profile]);
  /**
   * Envia documento (arquivo real) — delega ao helper compartilhado
   * `sendDocumentToConversation` (mesma rota usada pelo compartilhamento de
   * proposta em PDF), aplicando aqui apenas as atualizações otimistas da lista.
   */
  const handleSendDocument = useCallback(async (file: File, customFileName?: string) => {
    if (!conversation || !user) return;

    const fileName = customFileName || file.name;

    try {
      await sendDocumentToConversation({
        conversation: {
          id: conversation.id,
          inbox_id: conversation.inbox_id,
          phone: conversation.contact?.phone || conversation.group?.external_id || '',
          contactId: conversation.contact?.id,
          providerSlug: conversation.inbox?.provider_slug,
        },
        userId: user.id,
        file,
        fileName,
        mimeType: file.type,
        onMessageCreated: (messageData, attachmentData) => {
          // OTIMISTA: adiciona a mensagem imediatamente como 'sent'
          const optimisticMessage: Message = {
            id: messageData.id,
            conversation_id: messageData.conversation_id,
            sender_type: messageData.sender_type,
            sender_id: messageData.sender_id,
            content: messageData.content,
            message_type: messageData.message_type,
            direction: messageData.direction,
            status: 'sent',
            sent_at: messageData.sent_at,
            delivered_at: null,
            read_at: null,
            external_id: null,
            reply_to_id: null,
            attachments: [{ id: 'temp-0', ...attachmentData, metadata: null }],
            reply_to: null,
            reactions: null,
            metadata: null,
            deleted_at: null,
            participant_phone: null,
            // Dados do remetente (agente logado) para exibição imediata
            sender: {
              id: user.id,
              full_name: profile?.full_name || null,
              avatar_url: profile?.avatar_url || null,
            },
          };
          setMessages(prev => [...prev, optimisticMessage]);
        },
        onStatusResolved: (messageId, status, externalId, metadata) => {
          setMessages(prev => prev.map(m =>
            m.id === messageId
              ? { ...m, status, external_id: externalId, metadata: metadata || m.metadata }
              : m
          ));
          if (status === 'failed') {
            toast.error('Erro ao enviar documento via WhatsApp');
          }
        },
      });
    } catch (error) {
      chatLogger.error('Erro ao enviar documento:', error);
      toast.error('Erro ao enviar documento');
    }
  }, [conversation, user, profile]);


  /**
   * Envia áudio
   */
  const handleSendAudio = useCallback(async (audioBlob: Blob, duration: number) => {
    if (!conversation || !user) return;

    // Declara messageData fora do try para acesso no catch
    let messageData: any = null;

    try {
      // Prepara o áudio conforme o provider: OGG/Opus nativo ou MP3 (Meta WABA)
      const prepared = await prepareAudioForProvider(
        audioBlob,
        conversation.inbox?.provider_slug,
      );

      const timestamp = Date.now();
      const storagePath = `Chats/${conversation.id}/audio_${timestamp}.${prepared.extension}`;
      
      const { error: uploadError } = await supabase.storage
        .from('anexos')
        .upload(storagePath, prepared.blob, {
          contentType: prepared.mimeType,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      const { data: publicUrlData } = supabase.storage
        .from('anexos')
        .getPublicUrl(storagePath);

      let insertError: any = null;
      ({ data: messageData, error: insertError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_type: 'agent',
          sender_id: user.id,
          content: '🎤 Áudio',
          message_type: 'audio',
          direction: 'outbound',
          status: 'pending',
        })
        .select()
        .single());

      if (insertError) throw insertError;

      const attachmentData = {
        message_id: messageData.id,
        type: 'audio' as const,
        url: storagePath,
        mime_type: prepared.mimeType,
        size_bytes: prepared.blob.size,
        metadata: { seconds: duration, ptt: prepared.isVoiceNote },
      };

      await supabase.from('message_attachments').insert(attachmentData);


      // OTIMISTA: Adiciona mensagem ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type,
        direction: messageData.direction,
        status: 'sent', // Otimista
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        reply_to_id: null,
        attachments: [{ id: 'temp-0', ...attachmentData, filename: null }],
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: conversation.inbox_id,
          phone: conversation.contact?.phone || conversation.group?.external_id || '',
          message_type: 'audio',
          media_url: publicUrlData.publicUrl,
          waveform: prepared.isVoiceNote,
          delay_typing: getRandomDelay(1, 8),
          // MIME real do arquivo: WAHA exige 'audio/ogg; codecs=opus'; MP3 só no fallback
          media_mime_type: prepared.mode === 'basic_mp3' ? 'audio/mpeg' : 'audio/ogg; codecs=opus',

          // Campos de rastreabilidade para observabilidade
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: conversation.id,
          contact_id: conversation.contact?.id,
        },
      });

      if (sendError || !sendResult?.success) {
        await supabase.from('messages').update({ 
          status: 'failed',
          metadata: createErrorMetadata(sendError, sendResult)
        }).eq('id', messageData.id);
        // OTIMISTA: Reverte para 'failed' no state local
        setMessages(prev => prev.map(m => 
          m.id === messageData.id ? { ...m, status: 'failed' } : m
        ));
        
        // Registro assíncrono no catálogo de erros (fire-and-forget)
        registerSendError({
          errorCode: sendResult?.error_code || (sendError as any)?.code || '500',
          provider: conversation.inbox?.provider_slug || 'unknown',
          inboxId: conversation.inbox_id,
          messageId: messageData.id,
          conversationId: conversation.id,
          messageType: 'audio',
          rawError: sendResult || sendError,
          providerMessage: sendResult?.error || (sendError as Error)?.message,
        });
        
        toast.error('Erro ao enviar áudio via WhatsApp');
      } else {
        const updateData: { status: 'sent'; external_id?: string; metadata?: { providerMessageId: string } } = {
          status: 'sent',
          external_id: sendResult.external_id,
        };
        if (sendResult.provider_message_id) {
          updateData.metadata = { providerMessageId: sendResult.provider_message_id };
        }
        await supabase.from('messages').update(updateData).eq('id', messageData.id);
        // OTIMISTA: Atualiza external_id no state local
        setMessages(prev => prev.map(m => 
          m.id === messageData.id 
            ? { ...m, external_id: sendResult.external_id, metadata: updateData.metadata || null } 
            : m
        ));
      }
    } catch (error) {
      chatLogger.error('Erro ao enviar áudio:', error);
      toast.error(error instanceof Error ? error.message : 'Erro ao enviar áudio');
      // Se messageData existe (erro ocorreu após insert), grava metadata de erro no banco
      if (messageData?.id) {
        await supabase.from('messages').update({
          status: 'failed',
          metadata: createErrorMetadata(error)
        }).eq('id', messageData.id);
      }
    }
  }, [conversation, user]);

  /**
   * Envia localização
   */
  const handleSendLocation = useCallback(async (location: {
    latitude: number;
    longitude: number;
    name?: string;
    address?: string;
  }) => {
    if (!conversation || !user) return;

    try {
      const { data: messageData, error: insertError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_type: 'agent',
          sender_id: user.id,
          content: location.name || location.address || '📍 Localização',
          message_type: 'location',
          direction: 'outbound',
          status: 'pending',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const attachmentData = {
        message_id: messageData.id,
        type: 'location' as const,
        url: `https://maps.google.com/?q=${location.latitude},${location.longitude}`,
        metadata: {
          latitude: location.latitude,
          longitude: location.longitude,
          name: location.name,
          address: location.address,
        },
      };

      await supabase.from('message_attachments').insert(attachmentData);

      // OTIMISTA: Adiciona mensagem ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type,
        direction: messageData.direction,
        status: 'sent', // Otimista
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        reply_to_id: null,
        attachments: [{ id: 'temp-0', ...attachmentData, mime_type: null, filename: null, size_bytes: null }],
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: conversation.inbox_id,
          phone: conversation.contact?.phone || conversation.group?.external_id || '',
          message_type: 'location',
          latitude: location.latitude,
          longitude: location.longitude,
          location_name: location.name,
          location_address: location.address,
          // Campos de rastreabilidade para observabilidade
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: conversation.id,
          contact_id: conversation.contact?.id,
        },
      });

      if (sendError || !sendResult?.success) {
        await supabase.from('messages').update({ status: 'failed' }).eq('id', messageData.id);
        // OTIMISTA: Reverte para 'failed' no state local
        setMessages(prev => prev.map(m => 
          m.id === messageData.id ? { ...m, status: 'failed' } : m
        ));
        
        // Registro assíncrono no catálogo de erros (fire-and-forget)
        registerSendError({
          errorCode: sendResult?.error_code || (sendError as any)?.code || '500',
          provider: conversation.inbox?.provider_slug || 'unknown',
          inboxId: conversation.inbox_id,
          messageId: messageData.id,
          conversationId: conversation.id,
          messageType: 'location',
          rawError: sendResult || sendError,
          providerMessage: sendResult?.error || (sendError as Error)?.message,
        });
        
        toast.error('Erro ao enviar localização via WhatsApp');
      } else {
        const updateData: { status: 'sent'; external_id?: string; metadata?: { providerMessageId: string } } = {
          status: 'sent',
          external_id: sendResult.external_id,
        };
        if (sendResult.provider_message_id) {
          updateData.metadata = { providerMessageId: sendResult.provider_message_id };
        }
        await supabase.from('messages').update(updateData).eq('id', messageData.id);
        // OTIMISTA: Atualiza external_id no state local
        setMessages(prev => prev.map(m => 
          m.id === messageData.id 
            ? { ...m, external_id: sendResult.external_id, metadata: updateData.metadata || null } 
            : m
        ));
      }
    } catch (error) {
      chatLogger.error('Erro ao enviar localização:', error);
      toast.error('Erro ao enviar localização');
    }
  }, [conversation, user]);

  /**
   * Envia contato
   */
  const handleSendContact = useCallback(async (contact: {
    name: string;
    phone: string;
    businessDescription?: string;
  }) => {
    if (!conversation || !user) return;

    try {
      const { data: messageData, error: insertError } = await supabase
        .from('messages')
        .insert({
          conversation_id: conversation.id,
          sender_type: 'agent',
          sender_id: user.id,
          content: `👤 ${contact.name}`,
          message_type: 'contact',
          direction: 'outbound',
          status: 'pending',
        })
        .select()
        .single();

      if (insertError) throw insertError;

      const attachmentData = {
        message_id: messageData.id,
        type: 'contact' as const,
        url: `tel:${contact.phone.replace(/\D/g, '')}`,
        metadata: {
          displayName: contact.name,
          phone: contact.phone,
          businessDescription: contact.businessDescription,
        },
      };

      await supabase.from('message_attachments').insert(attachmentData);

      // OTIMISTA: Adiciona mensagem ao state local imediatamente como 'sent'
      const optimisticMessage: Message = {
        id: messageData.id,
        conversation_id: messageData.conversation_id,
        sender_type: messageData.sender_type,
        sender_id: messageData.sender_id,
        content: messageData.content,
        message_type: messageData.message_type,
        direction: messageData.direction,
        status: 'sent', // Otimista
        sent_at: messageData.sent_at,
        delivered_at: null,
        read_at: null,
        external_id: null,
        reply_to_id: null,
        attachments: [{ id: 'temp-0', ...attachmentData, mime_type: null, filename: null, size_bytes: null }],
        reply_to: null,
        reactions: null,
        metadata: null,
        deleted_at: null,
        participant_phone: null,
        // Dados do remetente (agente logado) para exibição imediata do avatar/nome
        sender: {
          id: user.id,
          full_name: profile?.full_name || null,
          avatar_url: profile?.avatar_url || null,
        },
      };
      setMessages(prev => [...prev, optimisticMessage]);

      const { data: sendResult, error: sendError } = await supabase.functions.invoke('send-message', {
        body: {
          inbox_id: conversation.inbox_id,
          phone: conversation.contact?.phone || conversation.group?.external_id || '',
          message_type: 'contact',
          contact_name: contact.name,
          contact_phone: contact.phone,
          contact_business_description: contact.businessDescription,
          // Campos de rastreabilidade para observabilidade
          source_type: 'chat',
          message_id: messageData.id,
          conversation_id: conversation.id,
          contact_id: conversation.contact?.id,
        },
      });

      if (sendError || !sendResult?.success) {
        await supabase.from('messages').update({ status: 'failed' }).eq('id', messageData.id);
        // OTIMISTA: Reverte para 'failed' no state local
        setMessages(prev => prev.map(m => 
          m.id === messageData.id ? { ...m, status: 'failed' } : m
        ));
        
        // Registro assíncrono no catálogo de erros (fire-and-forget)
        registerSendError({
          errorCode: sendResult?.error_code || (sendError as any)?.code || '500',
          provider: conversation.inbox?.provider_slug || 'unknown',
          inboxId: conversation.inbox_id,
          messageId: messageData.id,
          conversationId: conversation.id,
          messageType: 'contact',
          rawError: sendResult || sendError,
          providerMessage: sendResult?.error || (sendError as Error)?.message,
        });
        
        toast.error('Erro ao enviar contato via WhatsApp');
      } else {
        const updateData: { status: 'sent'; external_id?: string; metadata?: { providerMessageId: string } } = {
          status: 'sent',
          external_id: sendResult.external_id,
        };
        if (sendResult.provider_message_id) {
          updateData.metadata = { providerMessageId: sendResult.provider_message_id };
        }
        await supabase.from('messages').update(updateData).eq('id', messageData.id);
        // OTIMISTA: Atualiza external_id no state local
        setMessages(prev => prev.map(m => 
          m.id === messageData.id 
            ? { ...m, external_id: sendResult.external_id, metadata: updateData.metadata || null } 
            : m
        ));
      }
    } catch (error) {
      chatLogger.error('Erro ao enviar contato:', error);
      toast.error('Erro ao enviar contato');
    }
  }, [conversation, user]);

  /**
   * Envia reação
   */
  const handleSendReaction = useCallback(async (message: Message, emoji: string) => {
    if (!conversation || !message.external_id) return;

    // =========================================================================
    // PASSO 1: Calcular reações ANTES da chamada API (padrão otimista)
    // =========================================================================
    const metadata = message.metadata as { z_api_phone?: string; providerMessageId?: string } | null;
    const phone = metadata?.z_api_phone || conversation.contact?.phone || conversation.group?.external_id || '';

    const reactedBy = conversation.inbox.identifier?.replace(/\D/g, '') || 'agent';

    // Salvar estado anterior para rollback
    const previousReactions = message.reactions || [];

    // Processar reações: remover reação anterior do mesmo agente
    const filteredReactions = previousReactions.filter(
      r => normalizeId(r.reacted_by) !== normalizeId(reactedBy)
    );

    // Se tem emoji, adiciona; se vazio, apenas remove
    const updatedReactions: MessageReaction[] = emoji
      ? [...filteredReactions, {
          emoji,
          reacted_by: reactedBy,
          reacted_at: Date.now()
        }]
      : filteredReactions;

    // =========================================================================
    // PASSO 2: Atualizar UI IMEDIATAMENTE (~0ms) - padrão otimista
    // =========================================================================
    setMessages(prev => prev.map(m =>
      m.id === message.id
        ? { ...m, reactions: updatedReactions }
        : m
    ));

    // =========================================================================
    // PASSO 3: Background - enviar para API e banco, rollback se falhar
    // =========================================================================
    try {
      // Para Notificame (WhatsApp Oficial), usar providerMessageId do metadata (Base64)
      const isNotificame = conversation.inbox?.channel_type === 'whatsapp_oficial';
      const reactionMessageId = (isNotificame && metadata?.providerMessageId)
        ? metadata.providerMessageId
        : message.external_id;

      chatLogger.debug('Enviando reação (otimista)', {
        isNotificame,
        reactionMessageId: reactionMessageId?.substring(0, 30),
        emoji
      });

      // Passa contact_id para Instagram que não usa phone (usa channel_ids.instagram_igsid)
      const result = await sendReaction(
        conversation.inbox_id,
        phone,
        reactionMessageId,
        emoji,
        message.direction,
        conversation.contact?.id
      );

      if (result.success) {
        // API OK - persistir no banco de dados
        await supabase
          .from('messages')
          .update({ reactions: updatedReactions as any })
          .eq('id', message.id);
      } else {
        // API falhou - ROLLBACK da UI
        setMessages(prev => prev.map(m =>
          m.id === message.id
            ? { ...m, reactions: previousReactions }
            : m
        ));
        toast.error('Erro ao enviar reação');
      }
    } catch (error) {
      // Exceção - ROLLBACK da UI
      chatLogger.error('Erro ao enviar reação:', error);
      setMessages(prev => prev.map(m =>
        m.id === message.id
          ? { ...m, reactions: previousReactions }
          : m
      ));
      toast.error('Erro ao enviar reação');
    }
  }, [conversation, sendReaction]);

  /**
   * Deleta mensagem
   */
  const handleDeleteMessage = useCallback(async (message: Message, deleteForEveryone: boolean) => {
    if (!conversation) return;

    try {
      let deletedOnWhatsApp = false;

      if (deleteForEveryone && message.external_id && message.direction === 'outbound') {
        const metadata = message.metadata as { z_api_phone?: string } | null;
        const phone = metadata?.z_api_phone || conversation.contact?.phone || conversation.group?.external_id || '';
        
        const result = await deleteMessage(
          conversation.inbox_id,
          phone,
          message.external_id,
          true
        );
        
        deletedOnWhatsApp = result.deletedOnWhatsApp;
      }

      const deletedAt = new Date().toISOString();
      await supabase
        .from('messages')
        .update({ deleted_at: deletedAt })
        .eq('id', message.id);

      setMessages(prev => prev.map(m => 
        m.id === message.id 
          ? { ...m, deleted_at: deletedAt }
          : m
      ));

      if (deleteForEveryone) {
        if (deletedOnWhatsApp) {
          toast.success('Mensagem apagada para todos');
        } else {
          toast.success('Mensagem apagada localmente', {
            description: 'Não foi possível apagar no WhatsApp'
          });
        }
      } else {
        toast.success('Mensagem apagada para mim');
      }
    } catch (error) {
      chatLogger.error('Erro ao deletar mensagem:', error);
      toast.error('Erro ao deletar mensagem');
    }
  }, [conversation, deleteMessage]);

  /**
   * Atualiza status da conversa
   * Se marcando como SPAM, adiciona contato à blacklist LGPD automaticamente
   */
  const handleUpdateStatus = useCallback(async (status: ConversationStatus) => {
    if (!conversation) return;

    try {
      // ========================================
      // NOVO: Mensagem de sistema ao encerrar conversa
      // Informa o cliente no widget que a conversa foi encerrada
      // ========================================
      if (status === 'closed' && conversation.inbox?.channel_type === 'webchat') {
        // Nome do agente para exibir na mensagem
        const agentName = profile?.full_name || user?.email?.split('@')[0] || 'Agente';
        
        // Insere mensagem de sistema no banco
        const { data: sysMsg, error: sysMsgError } = await supabase
          .from('messages')
          .insert({
            conversation_id: conversation.id,
            sender_type: 'system',
            sender_id: user?.id || null,
            content: `Conversa encerrada por ${agentName}`,
            message_type: 'text',
            direction: 'outbound',
            status: 'sent',
            sent_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (!sysMsgError && sysMsg) {
          chatLogger.info('[handleUpdateStatus] Mensagem de sistema criada para encerramento', { messageId: sysMsg.id });
          
          // Dispara broadcast para o widget via send-message
          await supabase.functions.invoke('send-message', {
            body: {
              inbox_id: conversation.inbox_id,
              phone: '',
              content: `Conversa encerrada por ${agentName}`,
              message_type: 'text',
              message_id: sysMsg.id,
              conversation_id: conversation.id,
            },
          });
        } else if (sysMsgError) {
          chatLogger.warn('[handleUpdateStatus] Erro ao criar mensagem de sistema:', sysMsgError);
        }
      }

      // INTEGRAÇÃO LGPD: Se marcando como SPAM, adiciona contato à blacklist automaticamente
      if (status === 'spam' && conversation.contact?.phone) {
        const blacklistSuccess = await addToBlacklist(
          conversation.contact.phone,
          conversation.contact.name || undefined,
          'spam' // Motivo: Marcado como spam pelo agente
        );
        
        // Log de segurança (contato pode já estar na blacklist)
        if (!blacklistSuccess) {
          chatLogger.warn('[handleUpdateStatus] Contato não adicionado à blacklist (pode já existir):', 
            conversation.contact.phone
          );
        } else {
          chatLogger.info('[handleUpdateStatus] Contato adicionado à blacklist LGPD:', 
            conversation.contact.phone
          );
        }
      }

      // ========================================
      // Encerramento padrão: usa RPC close_conversation (padrão híbrido v2.1)
      // - Valida tenant via get_validated_tenant_id
      // - Bloqueia cross-tenant e registra em security_bypass_attempts
      // - Preenche closed_at = now() atomicamente
      // Para outros status (spam/pending) mantém UPDATE direto, pois a RPC
      // só cobre o caminho 'closed'.
      // ========================================
      if (status === 'closed') {
        // p_tenant_hint omitido: a RPC resolve via get_validated_tenant_id(NULL)
        // usando o tenant ativo do usuário autenticado (user_active_tenant).
        const { error } = await supabase.rpc('close_conversation', {
          p_conversation_id: conversation.id,
        });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('conversations')
          .update({
            status,
            closed_at: null,
          })
          .eq('id', conversation.id);
        if (error) throw error;
      }

      setConversation(prev => prev ? { ...prev, status } : null);
      toast.success(`Conversa marcada como ${status}`);
    } catch (error) {
      chatLogger.error('Erro ao atualizar status:', error);
      toast.error('Erro ao atualizar status');
    }
  }, [conversation, addToBlacklist, user, profile]);

  /**
   * Agente assume conversa da fila
   */
  const handleClaimConversation = useCallback(async () => {
    if (!conversation || !user) return;

    try {
      // 1. Atualiza a conversa (atribui ao agente e muda status para open)
      const { error } = await supabase
        .from('conversations')
        .update({ 
          assigned_to: user.id,
          status: 'open' as ConversationStatus,
        })
        .eq('id', conversation.id);

      if (error) throw error;

      // 2. Marca qualquer transferência pendente como aceita
      // IMPORTANTE: Garante que o card saia da coluna "Transferências" no Kanban
      const { error: transferError } = await supabase
        .from('conversation_transfers')
        .update({
          status: 'accepted',
          responded_at: new Date().toISOString(),
        })
        .eq('conversation_id', conversation.id)
        .eq('status', 'pending');

      // Log se falhar (não bloqueia fluxo - conversa já foi assumida)
      if (transferError) {
        chatLogger.warn('[handleClaimConversation] Erro ao atualizar transferência pendente:', transferError);
      } else {
        chatLogger.info('[handleClaimConversation] Transferência pendente marcada como aceita');
      }

      // ========================================
      // NOVO: Mensagem de sistema ao assumir conversa (webchat)
      // Informa o cliente no widget que um agente iniciou o atendimento
      // ========================================
      if (conversation.inbox?.channel_type === 'webchat') {
        const agentName = profile?.full_name || user?.email?.split('@')[0] || 'Agente';
        
        // Insere mensagem de sistema no banco
        const { data: sysMsg, error: sysMsgError } = await supabase
          .from('messages')
          .insert({
            conversation_id: conversation.id,
            sender_type: 'system',
            sender_id: user.id,
            content: `${agentName} iniciou o atendimento`,
            message_type: 'text',
            direction: 'outbound',
            status: 'sent',
            sent_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (!sysMsgError && sysMsg) {
          chatLogger.info('[handleClaimConversation] Mensagem de sistema criada para assumir conversa', { messageId: sysMsg.id });
          
          // Dispara broadcast para o widget via send-message
          await supabase.functions.invoke('send-message', {
            body: {
              inbox_id: conversation.inbox_id,
              phone: '',
              content: `${agentName} iniciou o atendimento`,
              message_type: 'text',
              message_id: sysMsg.id,
              conversation_id: conversation.id,
            },
          });
        } else if (sysMsgError) {
          chatLogger.warn('[handleClaimConversation] Erro ao criar mensagem de sistema:', sysMsgError);
        }
      }

      // 4. Atualiza estado local
      setConversation(prev => prev ? { 
        ...prev, 
        assigned_to: user.id,
        status: 'open' as ConversationStatus,
        assigned_agent: {
          id: user.id,
          full_name: user.user_metadata?.full_name || user.email || null,
          avatar_url: null,
        }
      } : null);

      toast.success('Conversa assumida com sucesso!');
    } catch (error) {
      chatLogger.error('[handleClaimConversation] Erro ao assumir conversa:', error);
      toast.error('Erro ao assumir conversa');
    }
  }, [conversation, user, profile]);

  /**
   * Indicador de digitação
   */
  const handleTyping = useCallback(() => {
    if (!conversation?.contact?.phone || !conversation?.inbox_id) return;
    
    if (typingTimeoutRef.current) return;
    
    sendTypingIndicator(conversation.inbox_id, conversation.contact.phone, {
      type: 'composing',
      duration: 3000,
    });
    
    typingTimeoutRef.current = setTimeout(() => {
      typingTimeoutRef.current = null;
    }, 3000);
  }, [conversation, sendTypingIndicator]);

  /**
   * Marca mensagens WhatsApp como lidas
   */
  const markWhatsAppAsRead = useCallback(async () => {
    if (!conversation?.inbox_id || !conversation?.contact?.phone) return;
    
    const unreadMessages = messages.filter(
      m => m.direction === 'inbound' && m.external_id && !m.read_at
    );
    
    const lastMessage = unreadMessages[unreadMessages.length - 1];
    if (lastMessage?.external_id) {
      markMessageAsRead(
        conversation.inbox_id,
        conversation.contact.phone,
        lastMessage.external_id
      );
    }
  }, [conversation, messages, markMessageAsRead]);

  // ==========================
  // EFEITOS
  // ==========================

  // Mantém messagesRef sincronizado com state (sem causar re-renders)
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Busca conversa quando conversationId muda
  // Reset de estado só ocorre aqui quando conversationId é explicitamente null
  useEffect(() => {
    if (conversationId) {
      // Reset refs para permitir novo fetch
      fetchingConversationRef.current = false;
      fetchingMessagesRef.current = false;
      fetchConversation();
      fetchMessages();
      fetchMessageCount(); // Busca contagem total de mensagens
    } else {
      // Reset estado apenas quando conversationId é explicitamente null (modal fechado)
      setConversation(null);
      setMessages([]);
      setTotalMessagesCount(null);
    }
  }, [conversationId, fetchConversation, fetchMessages, fetchMessageCount]);

  // Marca como lida quando conversa carrega (SOMENTE se NÃO for modo observador)
  useEffect(() => {
    if (conversation && !isObserverModeRef.current) {
      markAsRead();
    }
  }, [conversation?.id]);

  // Marca no WhatsApp como lido (SOMENTE se NÃO for modo observador)
  useEffect(() => {
    if (messages.length > 0 && !isObserverModeRef.current) {
      markWhatsAppAsRead();
    }
  }, [messages, markWhatsAppAsRead]);

  // Realtime para novas mensagens
  useEffect(() => {
    if (!conversationId) return;

    // Sufixo único por montagem evita conflito "cannot add postgres_changes after subscribe()"
    // quando o efeito remonta (StrictMode / reabertura do modal) antes do removeChannel completar.
    const channelName = `chat-modal-messages-${conversationId}-${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        async (payload) => {
          const newMessage = payload.new as Message;

          // SERVER-SIDE: hidrata mensagem completa via RPC (attachments + reply_to + sender)
          // Substitui múltiplos SELECTs no front, reduzindo carga no banco
          const { data: fullRows, error: fullErr } = await supabase.rpc('get_message_full_by_id', {
            p_message_id: newMessage.id,
            p_tenant_hint: currentTenant?.id ?? null,
          });

          if (fullErr) {
            chatLogger.warn('RPC get_message_full_by_id falhou no realtime INSERT', {
              error: fullErr.message,
              messageId: newMessage.id,
            });
          }

          const full = fullRows && fullRows.length > 0 ? (fullRows[0] as any) : null;

          const attachmentsArr = ((full?.attachments as any[]) || []).map((att: any) => ({
            id: att.id,
            type: att.type,
            url: att.url,
            mime_type: att.mime_type,
            filename: att.filename,
            size_bytes: att.size_bytes,
            metadata: (att.metadata ?? null) as Record<string, unknown> | null,
          }));

          const replyToData = full?.reply_to
            ? {
                id: full.reply_to.id,
                content: full.reply_to.content,
                message_type: full.reply_to.message_type,
                sender_type: full.reply_to.sender_type,
                attachments: (full.reply_to.attachments || []).map((att: any) => ({
                  ...att,
                  metadata: null,
                })),
              }
            : null;

          // Sender: prioriza fallback local (sender_id === user.id) para evitar lookup desnecessário
          let senderData: { id: string; full_name: string | null; avatar_url: string | null } | null = null;
          if (newMessage.sender_type === 'agent' && newMessage.sender_id) {
            if (newMessage.sender_id === user?.id && profile) {
              senderData = {
                id: user.id,
                full_name: profile.full_name || null,
                avatar_url: profile.avatar_url || null,
              };
            } else if (full?.sender) {
              senderData = {
                id: full.sender.id,
                full_name: full.sender.full_name,
                avatar_url: full.sender.avatar_url,
              };
            }
          }

          const messageWithAttachments: Message = {
            ...newMessage,
            attachments: attachmentsArr,
            reply_to: replyToData,
            sender: senderData,
          };

          // ----- Modo ancorado (jump-to-message) -----
          // Quando o usuário pulou para uma mensagem antiga via pesquisa,
          // NÃO fazemos append direto: incrementamos um contador e exibimos
          // a pílula "Ir para o fim" para o usuário voltar manualmente.
          // Mensagens próprias (outbound do agente logado) ainda são anexadas
          // para manter feedback imediato após o envio.
          const isOwnOutbound =
            newMessage.direction === 'outbound' &&
            newMessage.sender_type === 'agent' &&
            newMessage.sender_id === user?.id;

          if (isAnchoredModeRef.current && !isOwnOutbound) {
            setNewMessagesWhileAnchored(prev => prev + 1);
          } else {
            setMessages(prev => {
              if (prev.some(m => m.id === newMessage.id)) return prev;
              return [...prev, messageWithAttachments];
            });
          }
          
          // Se mensagem inbound chegou:
          // 1. Marca como lida imediatamente (SOMENTE se NÃO for modo observador)
          // 2. Atualiza last_inbound_at para recalcular janela 24h (WhatsApp Oficial e Instagram)
          if (newMessage.direction === 'inbound') {
            if (!isObserverModeRef.current) {
              markAsRead();
            }
            
            // REGRA: Comentários do Instagram NÃO resetam a janela de 24h (apenas DMs/stories)
            // Usa função centralizada para evitar divergência com Chat.tsx
            const eligible = isEligibleForSessionWindow(newMessage);
            
            if (eligible) {
              // Atualiza last_inbound_at imediatamente para que:
              // - O alerta "Janela 24h expirada" desapareça instantaneamente
              // - A seção Estatísticas mostre tempo restante correto
              // - useSessionWindow recalcule isExpired = false
              setConversation(prev => 
                prev ? { ...prev, last_inbound_at: newMessage.sent_at } : prev
              );
              
              chatLogger.debug('[Realtime] last_inbound_at atualizado via mensagem inbound (não-comentário)', {
                conversationId,
                newLastInbound: newMessage.sent_at,
              });
            } else {
              chatLogger.debug('[Realtime] Comentário recebido — last_inbound_at NÃO atualizado (sem impacto na janela 24h)', {
                conversationId,
                messageType: newMessage.message_type,
              });
            }
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'messages',
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          const updatedMessage = payload.new as Message;
          // Atualiza mensagem preservando dados enriquecidos que não vêm no Realtime UPDATE
          setMessages(prev =>
            prev.map(m => m.id === updatedMessage.id 
              ? { 
                  ...m, 
                  ...updatedMessage,
                  // Preserva dados de relacionamento que não vêm no payload do Realtime
                  reply_to: m.reply_to,
                  attachments: m.attachments,
                  sender: m.sender, // Preserva avatar e dados do agente remetente
                } 
              : m
            )
          );
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversationId, markAsRead, currentTenant?.id, user?.id, profile]);

  // ===== HIDRATAÇÃO TARDIA DE SENDER =====
  // Quando auth fica disponível após carregamento inicial, re-busca dados de sender
  // que falharam por race condition (RPC chamado antes de auth pronta)
  useEffect(() => {
    // Só executa se temos user autenticado E mensagens carregadas
    if (!user?.id || messages.length === 0) return;
    
    // Encontra mensagens de agente que têm sender_id mas não têm sender (dados)
    const messagesWithMissingSender = messages.filter(
      m => m.sender_type === 'agent' && m.sender_id && !m.sender
    );
    
    // Se não há mensagens com sender faltando, não faz nada
    if (messagesWithMissingSender.length === 0) return;
    
    chatLogger.debug('Hidratação tardia: encontradas mensagens sem sender', {
      count: messagesWithMissingSender.length,
    });
    
    // Coleta sender_ids únicos que precisam ser hidratados
    const senderIdsToHydrate = [...new Set(
      messagesWithMissingSender.map(m => m.sender_id as string)
    )];
    
    // Função async para hidratar senders
    const hydrateSenders = async () => {
      try {
        const { data: senders, error: sendersError } = await supabase
          .rpc('get_profiles_public_info', { p_user_ids: senderIdsToHydrate });
        
        if (sendersError) {
          chatLogger.warn('Hidratação tardia falhou', { error: sendersError.message });
          return;
        }
        
        if (!senders || senders.length === 0) return;
        
        // Cria mapa para lookup rápido
        const sendersMap = new Map<string, { id: string; full_name: string | null; avatar_url: string | null }>();
        senders.forEach((s: { id: string; full_name: string; avatar_url: string | null }) => {
          sendersMap.set(s.id, {
            id: s.id,
            full_name: s.full_name,
            avatar_url: s.avatar_url,
          });
        });
        
        // Aplica patch no state - preserva tudo exceto sender
        setMessages(prev => prev.map(m => {
          // Só atualiza mensagens que precisam de hidratação
          if (m.sender_type === 'agent' && m.sender_id && !m.sender) {
            const senderData = sendersMap.get(m.sender_id);
            if (senderData) {
              return { ...m, sender: senderData };
            }
          }
          return m;
        }));
        
        chatLogger.debug('Hidratação tardia concluída', {
          senderIds: senderIdsToHydrate.length,
          found: senders.length,
        });
      } catch (error) {
        chatLogger.error('Erro na hidratação tardia de senders', error);
      }
    };
    
    hydrateSenders();
  }, [user?.id, messages.length]); // Dispara quando auth fica pronta ou mensagens mudam

  // ===== Realtime subscription para atualizações de contatos =====
  // Necessário para atualizar is_saved_to_agenda em tempo real quando
  // o contato é salvo automaticamente na agenda pelo webhook
  useEffect(() => {
    const contactId = conversation?.contact?.id;
    if (!contactId) return;
    
    // Sufixo único por montagem evita conflito "cannot add postgres_changes after subscribe()"
    // quando o hook é instanciado em duplicidade (ex.: página /tenant/chat + ChatConversationModal
    // sobreposto via DealDetailsModalPremium, ou StrictMode remount).
    const channelName = `chat-contact-updates-${contactId}-${Math.random().toString(36).slice(2, 10)}`;
    const channel = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'contacts',
          filter: `id=eq.${contactId}`,
        },
        (payload) => {
          const updatedContact = payload.new as {
            id: string;
            name: string | null;
            phone: string | null;
            email: string | null;
            avatar_url: string | null;
            is_saved_to_agenda: boolean | null;
          };
          
          chatLogger.debug('[Realtime] Contato atualizado no hook:', {
            contactId,
            is_saved_to_agenda: updatedContact.is_saved_to_agenda,
          });
          
          // Atualiza o contato na conversa
          setConversation(prev => {
            if (!prev || !prev.contact) return prev;
            return {
              ...prev,
              contact: {
                ...prev.contact,
                name: updatedContact.name,
                phone: updatedContact.phone,
                email: updatedContact.email,
                avatar_url: updatedContact.avatar_url,
                is_saved_to_agenda: updatedContact.is_saved_to_agenda ?? false,
              },
            };
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          chatLogger.debug('[Realtime] Canal de contato conectado no hook:', contactId);
        }
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [conversation?.contact?.id]);

  return {
    // Estados
    conversation,
    messages,
    loading,
    messagesLoading,
    showSidebar,
    setShowSidebar,
    contactPresence,
    tenantId: currentTenant?.id,
    
    // Estados de paginação
    hasMoreMessages,
    loadingMoreMessages,
    totalMessagesCount,

    // Jump-to-message / Anchor window
    hasMoreNewer,
    loadingMoreNewer,
    isAnchoredMode,
    newMessagesWhileAnchored,
    jumpingToMessageId,

    // Handlers
    handleSendMessage,
    handleSendImages,
    handleSendVideo,
    handleSendDocument,
    handleSendAudio,
    handleSendLocation,
    handleSendContact,
    handleSendReaction,
    handleDeleteMessage,
    handleUpdateStatus,
    handleClaimConversation,
    handleTyping,

    // Paginação
    loadMoreMessages,
    loadMoreNewer,
    jumpToMessage,
    exitAnchorMode,

    
    // Funções auxiliares - expostas para refresh externo (ex: após salvar tags)
    fetchConversation,
    refetch: () => {
      fetchConversation();
      fetchMessages();
    },
  };
}
